/**
 * O que cada modelo traz do catálogo de campos.
 *
 * **Não é nota nem ranking.** Uma operadora que não manda `codigo_tiss` não é
 * pior — ela só não manda. O quadro existe para que isso se saiba **antes**:
 * sem ele, alguém abre a tela de glosas, não encontra a informação e conclui
 * que o sistema falhou, quando o arquivo nunca a trouxe.
 *
 * Só `guia` e `valor_pago` impedem a aprovação de um modelo — é a fronteira
 * que o cliente traçou: bloqueia o que impede gerar a baixa, e nada além.
 */
import { useEffect, useMemo, useState } from 'react';
import { mensagemErro } from '../api/axios';
import { coberturaDeModelos } from '../api/fluxfin';
import { Alerta, Badge, LoadingSpinner, Painel, Vazio } from './common';

const PAPEL = {
  recebimento: 'Recebimento',
  glosa: 'Glosa',
  recebimento_e_glosa: 'Recebimento e glosa',
};

export default function CoberturaCampos() {
  const [modelos, setModelos] = useState([]);
  const [aberto, setAberto] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    coberturaDeModelos()
      .then((r) => setModelos(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a cobertura.')))
      .finally(() => setCarregando(false));
  }, []);

  // Campos que nenhum modelo traz: é a lista do que ainda não dá para
  // oferecer em tela nenhuma, por falta de dado na origem.
  const lacunasGerais = useMemo(() => {
    if (modelos.length === 0) return [];
    const nunca = new Map();
    modelos.forEach((m) =>
      m.grupos.forEach((g) =>
        g.campos.forEach((c) => {
          if (!nunca.has(c.nome)) nunca.set(c.nome, true);
          if (c.presente) nunca.set(c.nome, false);
        })
      )
    );
    return [...nunca.entries()].filter(([, f]) => f).map(([n]) => n);
  }, [modelos]);

  if (carregando) return <LoadingSpinner />;

  return (
    <Painel
      titulo="Cobertura de campos"
      subtitulo="O que cada modelo traz do vocabulário do sistema — e o que não traz."
    >
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {modelos.length === 0 ? (
        <Vazio icone="bi-list-check" titulo="Nenhum modelo cadastrado" />
      ) : (
        <div className="p-3 d-flex flex-column gap-2">
          {lacunasGerais.length > 0 && (
            <div className="ff-aviso ff-aviso--info" style={{ margin: '0 0 6px' }}>
              <i className="bi bi-info-circle" aria-hidden="true" />
              <div>
                Nenhum modelo traz{' '}
                <strong className="ff-mono">{lacunasGerais.join(', ')}</strong>. Enquanto
                for assim, as telas que dependeriam desses campos ficam sem dado — não é
                falha do sistema, é ausência na origem.
              </div>
            </div>
          )}

          {modelos.map((m) => {
            const id = m.id_modelo_arquivo;
            const expandido = aberto === id;
            const faltaEssencial = m.faltando_essencial.length > 0;
            return (
              <div
                key={id}
                style={{
                  border: '1px solid var(--ff-border-soft)',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  className="w-100 d-flex flex-wrap align-items-center gap-3 p-3"
                  onClick={() => setAberto(expandido ? null : id)}
                  aria-expanded={expandido}
                  style={{ background: 'var(--ff-surface-2)', border: 0, textAlign: 'left' }}
                >
                  <i
                    className={`bi bi-chevron-${expandido ? 'down' : 'right'}`}
                    aria-hidden="true"
                  />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <strong>{m.operadora}</strong>
                    <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      {m.nome} v{m.versao} · {m.formato} · {PAPEL[m.papel] || m.papel}
                    </div>
                  </div>
                  <span className="ff-mono" style={{ fontSize: 13 }}>
                    {m.qtd_presentes}/{m.qtd_presentes + m.qtd_ausentes} campos
                  </span>
                  {faltaEssencial ? (
                    <Badge tom="reprovado">Falta campo essencial</Badge>
                  ) : (
                    <Badge tom="confirmado">Essenciais OK</Badge>
                  )}
                </button>

                {expandido && (
                  <div className="p-3 d-flex flex-column gap-3">
                    {faltaEssencial && (
                      <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                        <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                        <div>
                          Sem <strong>{m.faltando_essencial.join(', ')}</strong> este
                          modelo não consegue gerar baixa — é o único motivo pelo qual um
                          modelo é recusado.
                        </div>
                      </div>
                    )}

                    {m.grupos.map((g) => (
                      <div key={g.grupo}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--ff-muted)',
                            marginBottom: 4,
                          }}
                        >
                          {g.rotulo}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {g.campos.map((c) => (
                            <span
                              key={c.nome}
                              title={c.descricao}
                              className="ff-mono"
                              style={{
                                fontSize: 12,
                                padding: '3px 9px',
                                borderRadius: 20,
                                border: '1px solid var(--ff-border-soft)',
                                background: c.presente ? 'var(--ff-green-soft, #e8f5ee)' : 'transparent',
                                color: c.presente ? 'var(--ff-green-ink)' : 'var(--ff-muted)',
                                textDecoration: c.presente ? 'none' : 'none',
                                opacity: c.presente ? 1 : 0.7,
                              }}
                            >
                              <i
                                className={`bi bi-${c.presente ? 'check' : 'dash'} me-1`}
                                aria-hidden="true"
                              />
                              {c.nome}
                              {c.peso === 'essencial' && ' *'}
                              {c.sensivel && (
                                <i
                                  className="bi bi-shield-lock ms-1"
                                  title="Dado pessoal — nunca sai do sistema"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}

                    {m.campos_proprios.length > 0 && (
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--ff-muted)',
                            marginBottom: 4,
                          }}
                        >
                          Campos próprios desta operadora
                        </div>
                        <div style={{ fontSize: 13 }} className="ff-mono">
                          {m.campos_proprios.join(' · ')}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ff-muted)', marginTop: 2 }}>
                          Fora do vocabulário comum. Não é erro — é informação que só esta
                          operadora manda, guardada mesmo assim.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <p style={{ fontSize: 12, color: 'var(--ff-muted)', margin: 2 }}>
            <strong>*</strong> campo essencial: sem ele o modelo não é aprovado. Os demais
            são procurados pela IA no cadastro e, quando o arquivo não os traz, a ausência
            fica registrada aqui em vez de bloquear a operadora.
          </p>
        </div>
      )}
    </Painel>
  );
}
